import React from 'react';
import styled from 'styled-components';
import useAsyncLoad from '../../hooks/useAsyncLoad';

import htmlizeCarriageReturn from '../htmlizeCarriageReturn';
import { useTranslate } from '../../translate/I18n';
import { assetize } from '../../utils/pureUtils';
import companyRoutes from '../../routes/company';
import RegisterWithMission from '../auth/RegisterWithMission';
import TrustedByBanner from '../elements/trustedByBanner/TrustedByBanner';

import Box from '../atoms/Box';
import Button from '../atoms/Button';
import Img from '../atoms/Img';
import Text from '../atoms/Text';

import BulletParagraph from '../molecules/BulletParagraph';
import ExpandablePanel from '../molecules/ExpandablePanel';
import IlluBullet from '../molecules/IlluBullet';
import CtaSection from '../molecules/CtaSection';
import ProfileCount from '../molecules/ProfileCount';
import WhitePaper from '../molecules/WhitePaper';

import Users from '../atoms/icons/Users';
import Clock from '../atoms/icons/Clock';
import Search from '../atoms/icons/Search';
import { getSkillRoleBySlug } from '../../store/skills/skills.services';

import NotFound from '../auth/NotFound';
import SanitizedHTML from '../elements/SanitizedHTML';
import { useRouter, useParams } from '../../utils/router';

const { registerWithMission } = companyRoutes;

const StyledWaveImage = styled(Img)(({ theme }) => ({
  width: '100%',
  marginTop: theme.space[14]
}));

const StyledRightWaveImage = styled(Img)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  width: 'auto',
  zIndex: -1,
  [`@media (max-width: ${theme.breakpoints.lg})`]: {
    display: 'none'
  }
}));

const StyledDeveloperImage = styled(Img)(() => ({
  width: '100%'
}));

const getAllSkills = role => {
  const skills = [...role.skills];
  return skills.map(skill => ({ ...skill, level: 3 }));
};

// eslint-disable-next-line react/prop-types
const ProfileLandingPage = ({ preloadedRole = null }) => {
  const history = useRouter();
  const { categoryName, role: roleInUrl } = useParams();
  const slug = roleInUrl || categoryName;

  const { isLoading, result: role } = useAsyncLoad(async () => {
    if (preloadedRole === null) {
      return await getSkillRoleBySlug(slug);
    }
    return preloadedRole;
  }, [preloadedRole, slug]);

  const { t } = useTranslate([
    'landingPage',
    'registerWithMission',
    'mission',
    'profileCount',
    'skill',
    'level',
    `landingPage_${categoryName}`
  ]);

  const goToRegisterWithMission = () => {
    history.push(registerWithMission.to);
  };

  if (isLoading) {
    return null;
  }

  if (!role) {
    return <NotFound />;
  }

  return (
    <>
      <Box display="flex" flexDirection={{ _: 'column', lg: 'row' }}>
        <Box flex="0 0 50%" bg="whiteGrey" pl={{ _: 4, md: 6, lg: 12 }} pt={5} pr={4} pb={6}>
          <Box maxWidth={{ _: '520px', md: '720px', lg: '520px' }} margin="auto">
            <Text tag="h1" styleName="wH2" mb={2}>
              {t(`landingPage_${categoryName}:${role.key}.title`)}
            </Text>
            <Text styleName="wPBig" tag="" mb={14}>
              <SanitizedHTML
                tagElement="span"
                content={t(`landingPage_${categoryName}:${role.key}.desc`)} />
            </Text>
            <ProfileCount profileCount={role.nbMatchingFreelancers} />
          </Box>
        </Box>
        <Box flex="0 0 50%" pt={2} pb={8}>
          <Text maxWidth="800px" mx="auto" styleName="wH4" tag="h3" pl={{ _: 2, lg: 6 }} mt={4}>
            {t(`landingPage_${categoryName}:${role.key}.formTitle`)}
          </Text>
          <RegisterWithMission defaultSkills={getAllSkills(role)} />
        </Box>
      </Box>
      <TrustedByBanner />

      <Box textAlign="center" pt={14}>
        <Text styleName="wH2" mb={12}>
          {t('landingPage:howToHire')}
          {t(`landingPage_${categoryName}:${role.key}.displayName`)}
        </Text>
        <Box
          display="flex"
          margin="auto"
          maxWidth="1200px"
          flexDirection={{ _: 'column', md: 'row' }}
          justifyContent="space-between"
          px={{ _: 2, md: 6, lg: 12 }}>
          <BulletParagraph
            number={1}
            title={t(`landingPage_${categoryName}:${role.key}.process.title1`)}
            desc={t(`landingPage_${categoryName}:${role.key}.process.desc1`)}
          />
          <Box display="flex" flexDirection="column" height="100px" mt={6} mx={2}>
            <Text color="mainDark">&#8226;</Text>
            <Text color="mainDark">&#8226;</Text>
            <Text color="mainDark">&#8226;</Text>
          </Box>
          <BulletParagraph
            number={2}
            title={t(`landingPage_${categoryName}:${role.key}.process.title2`)}
            desc={t(`landingPage_${categoryName}:${role.key}.process.desc2`)}
          />
          <Box display="flex" flexDirection="column" height="100px" mt={6} mx={2}>
            <Text color="mainDark">&#8226;</Text>
            <Text color="mainDark">&#8226;</Text>
            <Text color="mainDark">&#8226;</Text>
          </Box>
          <BulletParagraph
            number={3}
            title={t(`landingPage_${categoryName}:${role.key}.process.title3`)}
            desc={t(`landingPage_${categoryName}:${role.key}.process.desc3`)}
          />
        </Box>
        <StyledWaveImage
          src={assetize('/lpwave.webp')}
          fallback={assetize('/lpwave.png')}
          alt="section wave decoration"
          lazyloading={true}
        />
      </Box>

      <Box py={14}>
        <Text styleName="wH2" textAlign="center" mb={14}>
          {t('landingPage:featuresTitle')}
        </Text>
        <Box display="flex" maxWidth="1200px" mx="auto" px={2} mb={6}>
          <Box flex="2">
            <IlluBullet mb={10} subtitle={t('landingPage:feature.title1')} description={t('landingPage:feature.desc1')}>
              <Box
                borderRadius="50%"
                border="2px solid"
                borderColor="greyVerylight"
                p={'14px'}
                pb={1}
                mb={{ _: 2, md: 0 }}>
                <Users fontSize="16px" color="mainDark" />
              </Box>
            </IlluBullet>

            <IlluBullet mb={10} subtitle={t('landingPage:feature.title2')} description={t('landingPage:feature.desc2')}>
              <Box
                borderRadius="50%"
                border="2px solid"
                borderColor="greyVerylight"
                p={'14px'}
                pb={1}
                mb={{ _: 2, md: 0 }}>
                <Clock fontSize="16px" color="mainDark" />
              </Box>
            </IlluBullet>

            <IlluBullet mb={10} subtitle={t('landingPage:feature.title3')} description={t('landingPage:feature.desc3')}>
              <Box
                borderRadius="50%"
                border="2px solid"
                borderColor="greyVerylight"
                p={'14px'}
                pb={1}
                mb={{ _: 2, md: 0 }}>
                <Search fontSize="16px" color="mainDark" />
              </Box>
            </IlluBullet>
          </Box>
          <Box flex="1" display={{ _: 'none', md: 'block' }}>
            <StyledDeveloperImage
              alt="man working on a desk"
              src={assetize('/developer.webp')}
              fallback={assetize('/developer.png')}
              lazyloading={true}
            />
          </Box>
        </Box>
        <Box textAlign="center">
          <Button
            data-cy="button-register-features"
            onClick={goToRegisterWithMission}
            mb={4}
            styleType="secondary"
            shape="rounded">
            {t(`landingPage_${categoryName}:${role.key}.hire`)}
          </Button>
        </Box>
      </Box>

      <WhitePaper />

      <Box bg="greyVerylight" py={14} position="relative" zIndex="1">
        <Box maxWidth="1200px" mx="auto" px={{ _: 2, md: 4 }} textAlign={{ _: 'center', md: 'left' }}>
          <Text styleName="wH2" mb={2}>
            {t('landingPage:startSearching', { value: role.displayName })}
          </Text>
          <Text mb={4}>{t(`landingPage_${categoryName}:${role.key}.hireDesc`)}</Text>
          <Button
            data-cy="button-register-start-searching"
            onClick={goToRegisterWithMission}
            mb={4}
            styleType="secondary"
            shape="rounded">
            {t(`landingPage_${categoryName}:${role.key}.hire`)}
          </Button>
          <StyledRightWaveImage
            alt="right wave decoration"
            src={assetize('/sidewave.webp')}
            fallback={assetize('/sidewave.png')}
            lazyloading={true}
          />
        </Box>
      </Box>

      <Box maxWidth="770px" margin="auto" py={12} px={2}>
        <Text styleName="wH3" mb={7}>
          {t(`landingPage_${categoryName}:${role.key}.faqTitle`)}
        </Text>

        <ExpandablePanel
          title={<Text styleName="wLabelBigPrimary">{t(`landingPage_${categoryName}:${role.key}.faqQuestion1`)}</Text>}>
          <Text>{htmlizeCarriageReturn(t(`landingPage_${categoryName}:${role.key}.faqResponse1`))}</Text>
        </ExpandablePanel>

        <ExpandablePanel
          title={<Text styleName="wLabelBigPrimary">{t(`landingPage_${categoryName}:${role.key}.faqQuestion2`)}</Text>}>
          <Text>
            <SanitizedHTML content={t(`landingPage_${categoryName}:${role.key}.faqResponse2`)} />
          </Text>
        </ExpandablePanel>

        <ExpandablePanel
          title={<Text styleName="wLabelBigPrimary">{t(`landingPage_${categoryName}:${role.key}.faqQuestion3`)}</Text>}>
          <Text>{htmlizeCarriageReturn(t(`landingPage_${categoryName}:${role.key}.faqResponse3`))}</Text>
        </ExpandablePanel>
      </Box>

      <CtaSection
        title={t('landingPage:hireForFree')}
        desc={t('landingPage:hireDesc')}
        ctaLabel={t(`landingPage_${categoryName}:${role.key}.hire`)}
        dataCy="button-register-hire-now"
        onClick={goToRegisterWithMission}
      />
    </>
  );
};

export default ProfileLandingPage;
